import { Link } from "react-router-dom";
import { Activity, Layers, ShieldCheck, Timer, LineChart, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Constellation, MirrorReflection, HeadlineRise, Magnetic } from "./motion";
import { SimBadge, NoticeText } from "./SimBadge";

const FEATURES = [
  [Activity, "Live NSE · BSE · MCX", "Real exchange prices, 5-level depth"],
  [Layers, "Option chain & strategies", "Greeks, payoff, one-click baskets"],
  [Timer, "GTT & price alerts", "Target, stop-loss, email alerts"],
  [ShieldCheck, "Zero real-money risk", "₹10,00,000 virtual capital"],
];
const STRIP = ["MARKET", "LIMIT", "SL", "SL-M", "AMO", "Bracket", "Cover", "Iceberg", "GTT OCO", "Basket", "Option chain", "Payoff", "Futures", "Reports · CSV", "Margin", "MTM"];

function HeroChart() {
  return (
    <svg viewBox="0 0 600 220" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id="gl" x1="0" x2="1"><stop offset="0" stopColor="hsl(46 95% 72%)" /><stop offset="1" stopColor="hsl(38 80% 45%)" /></linearGradient>
        <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="hsl(43 78% 54% / 0.28)" /><stop offset="1" stopColor="hsl(43 78% 54% / 0)" /></linearGradient>
      </defs>
      {[40, 90, 140, 190].map((y) => <line key={y} x1="0" x2="600" y1={y} y2={y} stroke="hsl(40 10% 100% / 0.05)" />)}
      <path d="M0 170 L40 160 L80 166 L120 140 L160 150 L200 118 L240 128 L280 96 L320 108 L360 80 L400 92 L440 60 L480 70 L520 44 L560 52 L600 30 L600 220 L0 220 Z" fill="url(#ga)" className="page-in" style={{ animationDelay: "1.4s" }} />
      <path d="M0 170 L40 160 L80 166 L120 140 L160 150 L200 118 L240 128 L280 96 L320 108 L360 80 L400 92 L440 60 L480 70 L520 44 L560 52 L600 30" fill="none" stroke="url(#gl)" strokeWidth="2.2" className="draw-line" />
      <circle cx="600" cy="30" r="4" fill="hsl(46 95% 72%)" className="pulse-soft" />
    </svg>
  );
}

function Cta() {
  const nav = useNavigate();
  return <Magnetic data-testid="landing-start-practising" onClick={() => nav("/register")} className="mt-6 h-11 px-6 rounded-full bg-gold text-[#0b0b0d] font-semibold text-[13px] shadow-gold">Start practising</Magnetic>;
}

function Showcase() {
  return (
    <div className="relative hidden lg:flex flex-col overflow-hidden border-r border-[hsl(40_10%_13%)] bg-[#050507]">
      <img src="/img/gold-lines.jpg" alt="" className="absolute inset-x-0 bottom-0 w-full h-[70%] object-cover object-bottom opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050507] via-[#050507]/80 to-[#050507]/30" />
      <div className="absolute inset-x-0 bottom-0 h-[45%] opacity-70"><MirrorReflection /></div>
      <div className="relative flex-1 flex flex-col justify-between p-12 xl:p-16 text-[#f4efe4]">
        <div />
        <div className="max-w-xl stagger">
          <div className="eyebrow !text-[hsl(43_60%_60%)] mb-5 flex items-center gap-2"><span className="h-px w-8 bg-[hsl(43_78%_54%)]" />The private terminal for Indian markets</div>
          <HeadlineRise className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            lines={[{ text: "Trade the real market." }, { text: "Risk none of it.", className: "font-serif-lux italic font-normal text-gold text-[1.12em]" }]} />
          <p className="mt-6 text-[#b9b2a3] text-[14px] leading-relaxed max-w-md">A professional simulated trading terminal on live exchange data, with an internal order-matching engine. Virtual money only — no order is ever sent to an exchange.</p>
          <Cta />
          <div className="mt-8 rounded-xl border border-[hsl(40_10%_100%/0.07)] bg-[hsl(225_25%_6%/0.6)] backdrop-blur-md p-4 float-slow [@media(max-height:920px)]:hidden">
            <div className="flex items-center justify-between text-[10px] font-mono tracking-widest text-[#8c8577] mb-2"><span className="flex items-center gap-1.5"><LineChart className="h-3 w-3 text-[hsl(43_78%_54%)]" />TERMINAL PREVIEW</span><span>ILLUSTRATIVE</span></div>
            <HeroChart />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 stagger">
          {FEATURES.map(([Icon, t, s]) => (
            <div key={t} className="group rounded-lg border border-[hsl(40_10%_100%/0.06)] bg-[hsl(225_25%_6%/0.55)] backdrop-blur-md p-3.5 hover:border-[hsl(43_78%_54%/0.45)] transition-colors duration-300">
              <Icon className="h-4 w-4 text-[hsl(43_78%_54%)] mb-2 transition-transform duration-300 group-hover:-translate-y-0.5" />
              <div className="text-[12.5px] font-semibold">{t}</div>
              <div className="text-[11px] text-[#8c8577] mt-0.5">{s}</div>
            </div>))}
        </div>
      </div>
      <div className="relative border-t border-[hsl(40_10%_100%/0.06)] bg-[#050507]/90 overflow-hidden h-9 flex items-center">
        <div className="marquee flex gap-8 whitespace-nowrap font-mono text-[10.5px] tracking-[0.18em] text-[#8c8577]">
          {[...STRIP, ...STRIP].map((s, i) => <span key={i} className="flex items-center gap-8">{s}<BarChart3 className="h-3 w-3 text-[hsl(43_78%_54%/0.6)]" /></span>)}
        </div>
      </div>
    </div>
  );
}

export function AuthLayout({ title, subtitle, children, footer, noticeTestid = "auth-notice-text" }) {
  return (
    <div className="min-h-full grid lg:grid-cols-[1.15fr_1fr] bg-background">
      <Showcase />
      <div className="relative flex items-center justify-center p-6 sm:p-12 overflow-hidden">
        <Constellation />
        <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[hsl(var(--gold)/0.10)] blur-3xl" />
        <div className="absolute inset-x-0 top-0 flex justify-center p-3 z-10"><SimBadge testid="auth-sim-badge" /></div>
        <div className="w-full max-w-sm page-in relative pt-8">
          <div className="eyebrow mb-3 flex items-center gap-2"><span className="h-px w-6 bg-primary" />Secure access</div>
          <h2 className="font-display font-bold text-3xl tracking-tight">{title}</h2>
          <p className="text-muted-foreground text-[13px] mt-1.5 mb-8">{subtitle}</p>
          <div className="rounded-xl border border-border glass p-6 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.8)] gold-hairline is-lit">{children}</div>
          <div className="mt-6 text-[12.5px] text-muted-foreground">{footer}</div>
          <p className="mt-10 text-[10.5px] text-muted-foreground leading-relaxed">No investment advice. Market data © respective exchanges.</p>
          <NoticeText testid={noticeTestid} className="mt-2" />
        </div>
      </div>
    </div>
  );
}

export const authInput = "w-full h-11 px-3.5 rounded-lg border border-input bg-background/60 text-[13px] outline-none focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)] transition-[border-color,box-shadow] duration-200";
export const authBtn = "w-full h-11 rounded-lg bg-gold text-[#0b0b0d] font-semibold text-[13px] tracking-wide shadow-gold hover:brightness-110 active:scale-[0.99] transition-[filter,transform] duration-150 disabled:opacity-60";
export { Link };
